/**
 * useSelfSignupLeads — leads criados pelo link publico de autocadastro do
 * professor autonomo (FASE P1.2, docs/plano-professor-v1.md).
 *
 * Reusa 100% o motor de leads existente (`leads` + `pipeline_stages`) —
 * "autocadastro" e apenas mais um `leads.source` (texto livre, sem CHECK).
 * "Pendente" = lead nesse source cujo stage atual ainda nao e terminal
 * (nem `is_won` nem `is_lost`).
 *
 * Aprovar = `convert-lead` `mode:'student'` (edge function existente, NAO
 * modificada aqui — contrato documentado em
 * `supabase/functions/convert-lead/index.ts`). Rejeitar = mover o lead pro
 * stage `is_lost=true` do clube (seedado automaticamente pelo trigger
 * `seed_club_pipeline_stages` em todo INSERT de `clubs`, incl. espacos de
 * professor) — sem coluna nova, sem RPC nova.
 */
import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { qk } from '@pps/query-keys';
import { cleanPayload, extractEdgeError } from '@pps/supabase/payload';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import i18n from '@/i18n';
import type { Lead, PipelineStage } from '@/types/crm';

export const SELF_SIGNUP_SOURCE = 'autocadastro';

const SELF_SIGNUP_LEADS_KEY = ['self-signup-leads'] as const;
const SELF_SIGNUP_COUNT_KEY = ['self-signup-leads-count'] as const;

export type SelfSignupLead = Lead & { stage: PipelineStage | null };

/**
 * Lista completa (nome/contato/esporte/notas) dos leads de autocadastro
 * ainda pendentes (stage aberto) do clube do professor logado. Usada pela
 * pagina `/professor/cadastros`.
 */
export function useSelfSignupLeads() {
  const { club } = useAuth();

  return useQuery({
    queryKey: [...SELF_SIGNUP_LEADS_KEY, club?.id],
    enabled: !!club?.id,
    queryFn: async (): Promise<SelfSignupLead[]> => {
      if (!club?.id) return [];
      const { data, error } = await supabase
        .from('leads')
        .select('*, stage:pipeline_stages(*)')
        .eq('club_id', club.id)
        .eq('source', SELF_SIGNUP_SOURCE)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as SelfSignupLead[];
      return rows.filter((lead) => lead.stage && !lead.stage.is_won && !lead.stage.is_lost);
    },
  });
}

/**
 * Contagem leve (badge da sidebar) de leads de autocadastro pendentes.
 * Espelha o padrao `usePendingMembersCount` (useClubMembers.ts): erros
 * retornam 0 silenciosamente (UX de badge tolera ausencia).
 *
 * Precisa de 2 queries porque "pendente" depende do stage (nao ha coluna
 * de status direta em `leads`): primeiro resolve os stage_ids "abertos"
 * do clube (poucas linhas, tipicamente < 10), depois um `count` head-only
 * sobre `leads` restrito a esses stages — evita trazer os leads inteiros
 * so pra contar.
 */
export function usePendingSelfSignupCount(clubId: string | undefined) {
  return useQuery({
    queryKey: [...SELF_SIGNUP_COUNT_KEY, clubId],
    enabled: !!clubId,
    queryFn: async (): Promise<number> => {
      if (!clubId) return 0;
      const { data: openStages, error: stagesError } = await supabase
        .from('pipeline_stages')
        .select('id')
        .eq('club_id', clubId)
        .eq('is_won', false)
        .eq('is_lost', false);
      if (stagesError || !openStages || openStages.length === 0) return 0;

      const { count, error } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('club_id', clubId)
        .eq('source', SELF_SIGNUP_SOURCE)
        .in('stage_id', openStages.map((s) => s.id));
      if (error) return 0;
      return count ?? 0;
    },
  });
}

/**
 * Lê `details.reason` do body de um FunctionsHttpError.
 *
 * Tem que ser chamado ANTES de `extractEdgeError`: aquele helper consome o
 * body original, e `Response.clone()` só funciona enquanto o body não foi
 * lido (mesmo cuidado de `Opportunities.tsx#handleConvertToStudent`). Body
 * não-JSON, sem `details` ou sem `context` → `null`, e o chamador segue o
 * tratamento genérico por código.
 */
async function lerMotivoDoErro(err: unknown): Promise<string | null> {
  const ctx = (err as { context?: Response } | null)?.context;
  if (!ctx || typeof ctx.clone !== 'function') return null;
  try {
    const raw: unknown = await ctx.clone().json();
    const reason = (raw as { details?: { reason?: unknown } } | null)?.details?.reason;
    return typeof reason === 'string' ? reason : null;
  } catch {
    return null;
  }
}

export interface ApproveSelfSignupInput {
  leadId: string;
  clubId: string;
  lessonId: string | null;
  tuitionPlanId: string | null;
  /** Dia de vencimento da matrícula (1..28, spec 2026-08-20 D5); NULL herda o plano. */
  billingDay: number | null;
}

export interface ApproveSelfSignupResult {
  studentId: string | null;
  invoiceId: string | null;
  lessonEnrollmentCreated: boolean;
}

/**
 * Aprova um lead de autocadastro: chama `convert-lead` `mode:'student'`
 * com o contrato existente (autenticado com o JWT do professor logado via
 * `supabase.functions.invoke`, NAO service role). `is_club_staff` ja trata
 * o owner do clube como staff — nenhuma mudanca de permissao necessaria.
 *
 * As telas de oportunidade (grid e drawer) convertem por
 * `useConverterLeadEmAluno` (hook unico: `ConvertLeadError` tipado e desfecho
 * lido em `lib/convert-lead-result`). Este caller continua com o `invoke`
 * direto de proposito: o dialogo de aprovacao reduz a resposta a
 * `{studentId, invoiceId, lessonEnrollmentCreated}` — nao le o desfecho nem
 * abre dialogo de reativacao —, e migrar traria o contrato inteiro para
 * devolver tres campos. `cleanPayload` remove `enrollment`/`tuition_plan_id`
 * quando `null` (o schema Zod da function usa `.nullish()`, entao a chave
 * OMITIDA e equivalente a `null` — ver `supabase/functions/_shared/zod.ts`).
 *
 * LIMITACAO CONHECIDA: em clube `unified_asaas`/`split_asaas`, aprovar COM
 * plano exige CPF — a function recusa com `cpf_obrigatorio_asaas` quando vai
 * nascer matricula nova e a plataforma cobra pelo Asaas — e este dialogo
 * ainda nao tem campo de CPF. O professor ve `cpfRequiredAsaas` e so aprova
 * sem plano ou depois de gravar o CPF no cadastro. Campo de CPF aqui e
 * tarefa propria.
 */
export function useApproveSelfSignupLead() {
  return useDomainMutation<ApproveSelfSignupResult, Error, ApproveSelfSignupInput>({
    mutationFn: async ({ leadId, clubId, lessonId, tuitionPlanId, billingDay }) => {
      const enrollment = (lessonId || billingDay != null)
        ? { lesson_id: lessonId, tuition_plan_id: tuitionPlanId, billing_day: billingDay ?? null }
        : null;

      const { data, error } = await supabase.functions.invoke('convert-lead', {
        body: cleanPayload({
          mode: 'student',
          lead_id: leadId,
          club_id: clubId,
          opportunity_id: null,
          dependent_id: null,
          tuition_plan_id: tuitionPlanId,
          enrollment,
        }),
      });

      if (error) {
        // Os dois motivos de CPF são `invalid_request` que o professor
        // consegue resolver sozinho — o fluxo de aprovação não tem campo de
        // CPF, então em clube cobrado pela plataforma (Asaas) ele precisa
        // saber que é ISSO que faltou, e não ler um "erro" genérico e tentar
        // de novo. `lerMotivoDoErro` vem antes de `extractEdgeError` porque
        // este consome o body.
        const motivo = await lerMotivoDoErro(error);
        if (motivo === 'cpf_obrigatorio_asaas') throw new Error(i18n.t('opportunities.cpfRequiredAsaas'));
        if (motivo === 'cpf_invalido') throw new Error(i18n.t('opportunities.cpfInvalid'));
        const code = await extractEdgeError(error);
        if (code === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
        if (code === 'forbidden') throw new Error(i18n.t('common.forbidden'));
        if (code === 'external_service') throw new Error(i18n.t('common.serviceError'));
        throw error;
      }

      const dataObj = data as {
        error?: string;
        student_id?: string | null;
        invoice_id?: string | null;
        lesson_enrollment_created?: boolean;
      } | null | undefined;
      if (dataObj?.error) {
        const code = await extractEdgeError(dataObj);
        if (code === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
        if (code === 'forbidden') throw new Error(i18n.t('common.forbidden'));
        throw new Error(dataObj.error);
      }

      return {
        studentId: dataObj?.student_id ?? null,
        invoiceId: dataObj?.invoice_id ?? null,
        lessonEnrollmentCreated: !!dataObj?.lesson_enrollment_created,
      };
    },
    invalidates: [
      SELF_SIGNUP_LEADS_KEY,
      SELF_SIGNUP_COUNT_KEY,
      ['students'],
      ['leads'],
      ['pipeline'],
      ['student-invoices'],
      qk.lessons.all,
    ],
    toast: { success: i18n.t('professor.cadastros.approveSuccess'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'approve_self_signup_lead' },
  });
}

export interface RejectSelfSignupInput {
  leadId: string;
  clubId: string;
}

/**
 * Rejeita um lead de autocadastro: move pro stage `is_lost=true` do
 * clube. Mesmo mecanismo que `convert-lead` usa pra marcar "Ganho" — so
 * que pro lado "Perdido". Sem coluna/RPC nova.
 */
export function useRejectSelfSignupLead() {
  return useDomainMutation<void, Error, RejectSelfSignupInput>({
    mutationFn: async ({ leadId, clubId }) => {
      const { data: lostStage, error: stageError } = await supabase
        .from('pipeline_stages')
        .select('id')
        .eq('club_id', clubId)
        .eq('is_lost', true)
        .limit(1)
        .maybeSingle();
      if (stageError) throw stageError;
      if (!lostStage) throw new Error(i18n.t('professor.cadastros.noLostStage'));

      const { error } = await supabase
        .from('leads')
        .update({ stage_id: lostStage.id, updated_at: new Date().toISOString() })
        .eq('id', leadId);
      if (error) throw error;
    },
    invalidates: [SELF_SIGNUP_LEADS_KEY, SELF_SIGNUP_COUNT_KEY, ['leads'], ['pipeline']],
    toast: { success: i18n.t('professor.cadastros.rejectSuccess'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'reject_self_signup_lead' },
  });
}
